import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAction, useMutation } from 'convex/react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../convex/_generated/api';
import { getErrorMessage } from '../../utils/errors';
import './RealtimeVoiceChat.css';

const OPENAI_REALTIME_CALLS_URL = 'https://api.openai.com/v1/realtime/calls';

const ASSISTANT_DELTA_EVENTS = new Set([
  'response.audio_transcript.delta',
  'response.text.delta',
  'response.output_text.delta',
]);

const ASSISTANT_DONE_EVENTS = new Set([
  'response.audio_transcript.done',
  'response.text.done',
  'response.output_text.done',
]);

function RealtimeVoiceChat({
  projectId,
  chatId,
  personaId,
  activeFilePath,
  embedded = false,
  title,
  description,
  onClose,
  onTranscriptSaved,
}) {
  const { t } = useTranslation();
  const createRealtimeVoiceSession = useAction(api.ai.createRealtimeVoiceSession);
  const saveMessage = useMutation(api.ai.saveMessage);

  const [sessionState, setSessionState] = useState('idle');
  const [statusLabel, setStatusLabel] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [conversation, setConversation] = useState([]);
  const [manualPrompt, setManualPrompt] = useState('');
  const [sessionMeta, setSessionMeta] = useState(null);

  const audioRef = useRef(null);
  const localStreamRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const dataChannelRef = useRef(null);
  const persistedIdsRef = useRef(new Set());
  const assistantDraftRef = useRef({ id: null, text: '' });
  const connectingRef = useRef(false);

  const canStart = sessionState === 'idle' || sessionState === 'error' || sessionState === 'stopped';
  const isLive = sessionState === 'live' || sessionState === 'connecting';

  const upsertConversation = useCallback((id, role, text, pending = false) => {
    if (!id || !text?.trim()) return;
    setConversation((current) => {
      const next = [...current];
      const index = next.findIndex((item) => item.id === id);
      const record = { id, role, text, pending };
      if (index >= 0) {
        next[index] = { ...next[index], ...record };
        return next;
      }
      return [...next, record];
    });
  }, []);

  const persistTranscript = useCallback(async (id, role, text) => {
    const normalized = text?.trim();
    if (!chatId || !normalized || persistedIdsRef.current.has(id)) return;
    persistedIdsRef.current.add(id);
    try {
      await saveMessage({
        chatId,
        role,
        content: normalized,
        personaId: role === 'assistant' ? personaId : undefined,
      });
      onTranscriptSaved?.();
    } catch (_error) {
      // Keep the live session running even if transcript sync fails.
    }
  }, [chatId, onTranscriptSaved, personaId, saveMessage]);

  const cleanupSession = useCallback(() => {
    const channel = dataChannelRef.current;
    if (channel) {
      try {
        channel.close();
      } catch (_error) {}
      dataChannelRef.current = null;
    }

    const peerConnection = peerConnectionRef.current;
    if (peerConnection) {
      try {
        peerConnection.ontrack = null;
        peerConnection.close();
      } catch (_error) {}
      peerConnectionRef.current = null;
    }

    const localStream = localStreamRef.current;
    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.srcObject = null;
    }

    assistantDraftRef.current = { id: null, text: '' };
    connectingRef.current = false;
  }, []);

  useEffect(() => () => cleanupSession(), [cleanupSession]);

  const stopSession = useCallback(() => {
    cleanupSession();
    setSessionState('stopped');
    setStatusLabel(t('accessibleStudio.voice.stopped', { defaultValue: 'Voice session ended.' }));
  }, [cleanupSession, t]);

  const sendRealtimeEvent = useCallback((payload) => {
    const channel = dataChannelRef.current;
    if (!channel || channel.readyState !== 'open') return false;
    channel.send(JSON.stringify(payload));
    return true;
  }, []);

  const handleRealtimeEvent = useCallback((event) => {
    if (!event?.type) return;

    if (event.type === 'error') {
      const nextMessage = event.error?.message || t('accessibleStudio.voice.genericError', {
        defaultValue: 'The voice mentor session ran into an issue.',
      });
      setErrorMessage(nextMessage);
      setSessionState('error');
      setStatusLabel(t('accessibleStudio.voice.retryHint', {
        defaultValue: 'Try reconnecting to start a new voice session.',
      }));
      return;
    }

    if (event.type === 'session.created' || event.type === 'session.updated') {
      setSessionState('live');
      setStatusLabel(t('accessibleStudio.voice.listening', {
        defaultValue: 'Listening. Speak naturally when you are ready.',
      }));
      return;
    }

    if (event.type === 'input_audio_buffer.speech_started') {
      setStatusLabel(t('accessibleStudio.voice.hearingYou', {
        defaultValue: 'Hearing you now...',
      }));
      return;
    }

    if (event.type === 'input_audio_buffer.speech_stopped') {
      setStatusLabel(t('accessibleStudio.voice.thinking', {
        defaultValue: 'Thinking about your request...',
      }));
      return;
    }

    if (event.type === 'conversation.item.input_audio_transcription.completed') {
      const transcript = event.transcript || event.item?.transcript || '';
      const itemId = event.item_id || event.item?.id || `user-${Date.now()}`;
      if (transcript.trim()) {
        upsertConversation(itemId, 'user', transcript, false);
        void persistTranscript(itemId, 'user', transcript);
      }
      return;
    }

    if (ASSISTANT_DELTA_EVENTS.has(event.type)) {
      const delta = event.delta || '';
      if (!delta) return;
      const itemId =
        event.item_id ||
        event.response_id ||
        event.response?.id ||
        assistantDraftRef.current.id ||
        `assistant-${Date.now()}`;
      const base = assistantDraftRef.current.id === itemId ? assistantDraftRef.current.text : '';
      const nextText = `${base}${delta}`;
      assistantDraftRef.current = { id: itemId, text: nextText };
      upsertConversation(itemId, 'assistant', nextText, true);
      return;
    }

    if (ASSISTANT_DONE_EVENTS.has(event.type)) {
      const itemId =
        event.item_id ||
        event.response_id ||
        event.response?.id ||
        assistantDraftRef.current.id ||
        `assistant-${Date.now()}`;
      const finalText =
        event.transcript ||
        event.text ||
        event.output_text ||
        assistantDraftRef.current.text ||
        '';
      if (finalText.trim()) {
        assistantDraftRef.current = { id: null, text: '' };
        upsertConversation(itemId, 'assistant', finalText, false);
        void persistTranscript(itemId, 'assistant', finalText);
      }
      setStatusLabel(t('accessibleStudio.voice.listening', {
        defaultValue: 'Listening. Speak naturally when you are ready.',
      }));
    }
  }, [persistTranscript, t, upsertConversation]);

  const startSession = useCallback(async () => {
    if (!projectId || connectingRef.current) return;
    connectingRef.current = true;
    setErrorMessage('');
    setStatusLabel(t('accessibleStudio.voice.connecting', {
      defaultValue: 'Connecting to the voice mentor...',
    }));
    setSessionState('connecting');

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error(t('accessibleStudio.voice.mediaUnsupported', {
          defaultValue: 'This browser does not support microphone access for live voice chat.',
        }));
      }

      const session = await createRealtimeVoiceSession({
        projectId,
        personaId,
        activeFilePath,
      });
      setSessionMeta(session);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      localStreamRef.current = stream;

      const pc = new RTCPeerConnection();
      peerConnectionRef.current = pc;

      if (audioRef.current) {
        audioRef.current.autoplay = true;
      }

      pc.ontrack = (trackEvent) => {
        if (audioRef.current) {
          audioRef.current.srcObject = trackEvent.streams[0];
        }
      };

      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      const channel = pc.createDataChannel('oai-events');
      dataChannelRef.current = channel;
      channel.addEventListener('message', (messageEvent) => {
        try {
          handleRealtimeEvent(JSON.parse(messageEvent.data));
        } catch (_error) {}
      });
      channel.addEventListener('open', () => {
        setSessionState('live');
        setStatusLabel(t('accessibleStudio.voice.ready', {
          defaultValue: 'Connected. The mentor is ready.',
        }));
        sendRealtimeEvent({
          type: 'response.create',
          response: {
            modalities: ['audio', 'text'],
            instructions: 'Greet the author briefly, then ask what they want help with right now.',
          },
        });
      });
      channel.addEventListener('close', () => {
        if (sessionState !== 'stopped') {
          setStatusLabel(t('accessibleStudio.voice.closed', {
            defaultValue: 'Voice session closed.',
          }));
          setSessionState('stopped');
        }
      });

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const sdpResponse = await fetch(OPENAI_REALTIME_CALLS_URL, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${session.clientSecret}`,
          'Content-Type': 'application/sdp',
        },
      });

      if (!sdpResponse.ok) {
        throw new Error(await sdpResponse.text());
      }

      const answer = {
        type: 'answer',
        sdp: await sdpResponse.text(),
      };
      await pc.setRemoteDescription(answer);
    } catch (error) {
      cleanupSession();
      setSessionState('error');
      setErrorMessage(getErrorMessage(error, t('accessibleStudio.voice.connectFailed', {
        defaultValue: 'Could not start the live voice mentor.',
      })));
      setStatusLabel(t('accessibleStudio.voice.retryHint', {
        defaultValue: 'Check microphone permissions and try again.',
      }));
    } finally {
      connectingRef.current = false;
    }
  }, [
    activeFilePath,
    cleanupSession,
    createRealtimeVoiceSession,
    handleRealtimeEvent,
    personaId,
    projectId,
    sendRealtimeEvent,
    sessionState,
    t,
  ]);

  const sendManualPrompt = useCallback(async () => {
    const trimmed = manualPrompt.trim();
    if (!trimmed || !sendRealtimeEvent) return;
    const localId = `typed-${Date.now()}`;
    upsertConversation(localId, 'user', trimmed, false);
    void persistTranscript(localId, 'user', trimmed);
    const created = sendRealtimeEvent({
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content: [
          {
            type: 'input_text',
            text: trimmed,
          },
        ],
      },
    });
    if (created) {
      sendRealtimeEvent({
        type: 'response.create',
        response: { modalities: ['audio', 'text'] },
      });
      setManualPrompt('');
      setStatusLabel(t('accessibleStudio.voice.thinking', {
        defaultValue: 'Thinking about your request...',
      }));
    }
  }, [manualPrompt, persistTranscript, sendRealtimeEvent, t, upsertConversation]);

  const panelTitle = title || t('accessibleStudio.title', { defaultValue: 'Accessible Studio' });
  const panelDescription = description || t('accessibleStudio.description', {
    defaultValue: 'Speak with your mentor in a voice-first workspace with live transcript support.',
  });

  const emptyTranscriptLabel = useMemo(() => (
    t('accessibleStudio.voice.emptyTranscript', {
      defaultValue: 'The live transcript will appear here once the conversation starts.',
    })
  ), [t]);

  return (
    <section className={`realtime-voice-chat ${embedded ? 'embedded' : 'full'}`}>
      <audio ref={audioRef} className="rvc-remote-audio" />

      <div className="rvc-header">
        <div>
          <p className="rvc-kicker">{t('accessibleStudio.voice.kicker', { defaultValue: 'Voice mentor' })}</p>
          <h2>{panelTitle}</h2>
          <p>{panelDescription}</p>
        </div>
        <div className="rvc-header-actions">
          {sessionMeta?.model && (
            <span className="rvc-meta-pill">{sessionMeta.model}</span>
          )}
          {onClose && (
            <button type="button" className="rvc-icon-btn" onClick={onClose} title={t('common.close', { defaultValue: 'Close' })}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="rvc-toolbar">
        <button
          type="button"
          className={`rvc-primary-btn ${isLive ? 'danger' : ''}`}
          onClick={isLive ? stopSession : startSession}
          disabled={sessionState === 'connecting'}
        >
          {isLive
            ? t('accessibleStudio.voice.endSession', { defaultValue: 'End voice session' })
            : t('accessibleStudio.voice.startSession', { defaultValue: 'Start voice chat' })}
        </button>
        <div className="rvc-status-block">
          <strong>{t('accessibleStudio.voice.status', { defaultValue: 'Status' })}</strong>
          <span>{statusLabel || t('accessibleStudio.voice.idle', { defaultValue: 'Waiting to start.' })}</span>
        </div>
      </div>

      {errorMessage && (
        <div className="rvc-alert error">
          {errorMessage}
        </div>
      )}

      <div className="rvc-transcript">
        {conversation.length === 0 ? (
          <div className="rvc-empty">
            <p>{emptyTranscriptLabel}</p>
          </div>
        ) : (
          conversation.map((item) => (
            <div key={item.id} className={`rvc-message ${item.role}`}>
              <span className="rvc-message-role">
                {item.role === 'assistant'
                  ? t('accessibleStudio.voice.mentor', { defaultValue: 'Mentor' })
                  : t('accessibleStudio.voice.you', { defaultValue: 'You' })}
              </span>
              <p>{item.text}{item.pending ? '...' : ''}</p>
            </div>
          ))
        )}
      </div>

      <div className="rvc-manual-input">
        <textarea
          value={manualPrompt}
          onChange={(event) => setManualPrompt(event.target.value)}
          rows={2}
          placeholder={t('accessibleStudio.voice.manualPrompt', {
            defaultValue: 'Type a prompt during the voice session if you want to steer the conversation.',
          })}
          disabled={!isLive}
        />
        <button
          type="button"
          className="rvc-secondary-btn"
          onClick={sendManualPrompt}
          disabled={!isLive || !manualPrompt.trim()}
        >
          {t('accessibleStudio.voice.sendText', { defaultValue: 'Send text prompt' })}
        </button>
      </div>
    </section>
  );
}

export default RealtimeVoiceChat;
